import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { supabase } from '@/lib/supabaseClient';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';

type VerifyResult =
  | {
      found: true;
      product_name: string;
      batch_no: string | null;
      mfg_date: string | null;
      expiry_date: string | null;
      first_verified_at: string;
      verify_count: number;
      is_suspicious: boolean;
    }
  | { found: false; serial?: string };

// The barcode under the hologram carries the box's stock serial (e.g.
// RXS-001792, GIFTBOX-000899). It is deliberately NOT a verification code, so
// a customer who types it must be pointed to the QR — not told the product is fake.
const SERIAL_RE = /^[A-Z]{2,10}-\d{3,8}$/;

export default function VerifyPage() {
  const { code: codeParam } = useParams<{ code?: string }>();
  const [code, setCode] = useState(codeParam ?? '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';

  const runVerify = useCallback(async (value: string) => {
    if (!value.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    const typed = value.trim().toUpperCase();
    if (SERIAL_RE.test(typed)) {
      setLoading(false);
      setResult({ found: false, serial: typed });
      return;
    }
    const { data, error: rpcError } = await supabase.rpc('core_verify_product_code', { p_code: value.trim() });
    setLoading(false);
    if (rpcError) {
      setError(t('verify.result.error'));
      return;
    }
    setResult(data as VerifyResult);
  }, [t]);

  useEffect(() => {
    if (codeParam) runVerify(codeParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only auto-run once, on the initial route param
  }, []);

  return (
    <>
      <SharedNavbar />
      <main className="w-full min-h-screen bg-white font-sans pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Hero */}
          <div className="flex flex-col items-center text-center gap-4 mb-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-[#7B1113]" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
                {t('verify.hero.label')}
              </span>
              <div className="w-8 h-px bg-[#7B1113]" />
            </div>

            <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#2A2A2A] leading-[1.15]">
              {t('verify.hero.heading')}
            </h1>
            <p className="text-sm text-[#8A8A8A] font-light max-w-md">
              {t('verify.hero.subtitle')}
            </p>
          </div>

          {/* Instructions + Code input */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            <div className="flex flex-col gap-5 p-8 rounded-2xl bg-[#F0EFED]">
              <h3 className="font-serif text-lg font-semibold text-[#2A2A2A]">
                {t('verify.instruction.title')}
              </h3>
              <ol className="flex flex-col gap-3 list-decimal list-inside">
                <li className="text-sm text-[#8A8A8A] font-light">{t('verify.instruction.step1')}</li>
                <li className="text-sm text-[#8A8A8A] font-light">{t('verify.instruction.step2')}</li>
                <li className="text-sm text-[#8A8A8A] font-light">{t('verify.instruction.step3')}</li>
                <li className="text-sm text-[#8A8A8A] font-light">{t('verify.instruction.step4')}</li>
              </ol>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                runVerify(code);
              }}
              className="flex flex-col gap-4 p-8 rounded-2xl bg-[#2D6A4F]/5 border border-[#2D6A4F]/10"
            >
              <label className="flex flex-col gap-2">
                <span className="text-[11px] tracking-[0.1em] uppercase text-[#8A8A8A] font-medium">
                  {t('verify.input.label')}
                </span>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder={t('verify.input.placeholder')}
                  className="w-full px-4 py-3 rounded-xl border border-[#2D6A4F]/20 bg-white text-sm font-mono tracking-wider text-[#2A2A2A] outline-none focus:border-[#2D6A4F]"
                />
              </label>
              <button
                type="submit"
                disabled={loading || !code.trim()}
                className="w-full px-7 py-3 bg-[#2D6A4F] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#1E4D3A] transition-colors cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('verify.input.checking') : t('verify.input.button')}
              </button>
            </form>
          </div>

          {/* Result */}
          {error && (
            <div className="mb-16 p-6 rounded-2xl bg-[#7B1113]/5 border border-[#7B1113]/15 text-center">
              <p className="text-sm text-[#7B1113]">{error}</p>
            </div>
          )}

          {result && !error && (
            <div className="mb-16">
              {result.found ? (
                <div className="flex flex-col gap-4">
                  <div className="p-8 rounded-2xl bg-[#2D6A4F]/5 border border-[#2D6A4F]/20 flex flex-col items-center text-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center">
                      <i className="ri-shield-check-fill text-3xl text-[#2D6A4F]"></i>
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-[#2A2A2A]">{t('verify.result.authentic.title')}</h3>
                    <p className="text-sm text-[#8A8A8A] font-light max-w-md">
                      {t('verify.result.authentic.body', { product: result.product_name })}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-[#F0EFED] divide-y divide-[#F0EFED]">
                    {[
                      [t('verify.result.field.product'), result.product_name],
                      [t('verify.result.field.batch'), result.batch_no],
                      [t('verify.result.field.mfgDate'), result.mfg_date ? new Date(result.mfg_date).toLocaleDateString() : null],
                      [t('verify.result.field.expiryDate'), result.expiry_date ? new Date(result.expiry_date).toLocaleDateString() : null],
                      [t('verify.result.field.firstScan'), new Date(result.first_verified_at).toLocaleDateString()],
                      [t('verify.result.field.scanCount'), String(result.verify_count)],
                      [t('verify.result.field.company'), t('verify.result.field.companyValue')],
                    ]
                      .filter(([, value]) => !!value)
                      .map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between px-5 py-3">
                        <span className="text-[12px] text-[#8A8A8A] font-light">{label}</span>
                        <span className="text-[12.5px] text-[#2A2A2A] font-medium">{value}</span>
                      </div>
                    ))}
                  </div>

                  {result.is_suspicious && (
                    <div className="p-5 rounded-2xl bg-[#B45309]/5 border border-[#B45309]/20 flex items-start gap-3">
                      <i className="ri-alert-fill text-xl text-[#B45309] flex-shrink-0 mt-0.5"></i>
                      <div>
                        <p className="text-[13px] font-semibold text-[#B45309]">{t('verify.result.suspicious.title')}</p>
                        <p className="text-[12px] text-[#8A8A8A] font-light mt-0.5">{t('verify.result.suspicious.body')}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : result.serial ? (
                <div className="p-8 rounded-2xl bg-[#B45309]/5 border border-[#B45309]/20 flex flex-col items-center text-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-[#B45309]/10 flex items-center justify-center">
                    <i className="ri-qr-scan-2-line text-3xl text-[#B45309]"></i>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-[#2A2A2A]">{t('verify.result.serial.title')}</h3>
                  <p className="text-sm text-[#8A8A8A] font-light max-w-md">{t('verify.result.serial.body', { code: result.serial })}</p>
                </div>
              ) : (
                <div className="p-8 rounded-2xl bg-[#7B1113]/5 border border-[#7B1113]/15 flex flex-col items-center text-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-[#7B1113]/10 flex items-center justify-center">
                    <i className="ri-error-warning-fill text-3xl text-[#7B1113]"></i>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-[#2A2A2A]">{t('verify.result.notfound.title')}</h3>
                  <p className="text-sm text-[#8A8A8A] font-light max-w-md">{t('verify.result.notfound.body')}</p>
                </div>
              )}
            </div>
          )}

          {/* Trust Section */}
          <div className="p-8 rounded-2xl bg-[#1A1A1A] text-center flex flex-col items-center gap-4">
            <h3 className="font-serif text-xl font-semibold text-white">
              {t('verify.trust.title')}
            </h3>
            <p className="text-sm text-white/40 font-light max-w-lg">
              {t('verify.trust.body')}
            </p>
            <Link
              to={prefix + '/products'}
              className="w-full sm:w-auto px-7 py-3 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center"
            >
              {t('verify.cta')}
            </Link>
          </div>
        </div>
      </main>
      <SharedFooter />
    </>
  );
}
